import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostListener,
  Inject,
  OnInit,
  TemplateRef,
  ViewChild
} from '@angular/core';
import { AbstractGroupedList } from '@hoc/hoc-grouped-list';
import {
  Project,
  projectDownloadMapper,
  ProjectFilterBuilder,
  projectGroupingOperator,
  updateProjectFilter
} from '@core/models/project';
import {
  EXACTLY_OPERATION,
  GalaxyDrawerBlockingService,
  ignorePromise,
  addParamForRoute,
  MenuItem,
  slideInFromRight
} from '@ebcont/ebcont.galaxy.js';
import { environment } from '@env/environment';
import { InjectorKeys } from '@core/utils/injector.keys';
import { ApplicationConfiguration } from '@core/services/configuration/application.configuration.interface';
import { ActivatedRoute, Router } from '@angular/router';
import { BackendService, TypeaheadItem } from '@core/prototypes/backend.service';
import {
  DateFilterParameters,
  FilterInstance,
  filterInstance,
  FILTERS,
  NumberFilterParameters
} from '@core/prototypes/filtering';
import { SIMPLE_EVENTS, textAsMenuItem, typeaheadsAsMenuItems } from '@core/utils/utils';
import { forkJoin, Observable } from 'rxjs';
import { Customer } from '@core/models/customer';
import { UntilDestroy } from '@ngneat/until-destroy';
import { ProjectsService } from '@core/services/employee/projects.service';
import { UserService } from '@core/services/user/user.service';
import { faDownload } from '@fortawesome/free-solid-svg-icons';
import { map } from 'rxjs/operators';
import { ListItem, ListItemColumnAlignment } from '@core/models/list-item.model';
import { formatNumber } from '@angular/common';
import { registerLocaleData } from '@angular/common';
import localeDe from '@angular/common/locales/de';
import localeDeExtra from '@angular/common/locales/extra/de';
import { TranslateService } from '@ngx-translate/core';

registerLocaleData(localeDe, 'de', localeDeExtra);

@UntilDestroy()
@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'app-project-list',
  templateUrl: './project-list.component.html',
  styleUrls: ['./project-list.component.css'],
  animations: [slideInFromRight],
  standalone: false
})
export class ProjectListComponent extends AbstractGroupedList<Project> implements OnInit {
  maxLabelsInFilters = 2;
  isAdmin = false;

  public selectedProject: Project | undefined;

  public filtersForBlock: FilterInstance[] = [];
  private initialized = false;
  public searchValue: MenuItem = { id: '', label: '', isRouterLink: false } as MenuItem;
  private projectCode = '';

  constructor(
    @Inject(InjectorKeys.PROJECTS_SERVICE) private service: BackendService<Project>,
    cd: ChangeDetectorRef,
    router: Router,
    route: ActivatedRoute,
    @Inject(InjectorKeys.CONFIGURATION) public config: ApplicationConfiguration,
    @Inject(InjectorKeys.CUSTOMERS_SERVICE) private customersService: BackendService<Customer>,
    private projectsService: ProjectsService,
    private userService: UserService,
    private translate: TranslateService
  ) {
    super(service, projectGroupingOperator, config, cd, router, route);
  }

  ngOnInit(): void {
    this.filter = new ProjectFilterBuilder(this.config).build();
    const customers = this.customersService.typeahead('');
    const projects = this.projectsService.technologies('');
    this.route.queryParams.subscribe((queryParams) => {
      const pageNumber = parseInt(queryParams['pageNumber'] || 0);
      const pageSize = parseInt(queryParams['pageSize'] || this.config.pageSize);
      this.projectCode = queryParams['projectCode'] || '';
      this.filter.pageNumber = pageNumber;
      this.filter.pageSize = pageSize;
      this.pagination.currentPage = pageNumber;
      this.pagination.pageSize = pageSize;

      this.onReloaded();
      this.cd.detectChanges();
    });

    this.userService.getAdminStatus().subscribe((isAdmin) => {
      this.isAdmin = isAdmin;
    });

    forkJoin({
      customers,
      projects
    }).subscribe({
      next: ({ customers, projects }) => {
        this.registerCustomersFilter(customers);
        this.registerProjectsFilter(projects);
      },
      complete: () => {
        this.initialized = true;
        this.cd.markForCheck();
        this.cd.detectChanges();
      }
    });
  }

  public handleEmptyList(): void {
    this.searchValue = textAsMenuItem('');
    this.applyFilter();
  }

  private registerCustomersFilter(customers: TypeaheadItem[]) {
    this.filtersForBlock = [
      filterInstance(FILTERS.PROJECT_CUSTOMERS, typeaheadsAsMenuItems(customers), {
        switchMode: true,
        maxLabels: this.maxLabelsInFilters,
        isMulti: false,
        searchable: true
      })
    ];
  }

  private registerProjectsFilter(projects: string[]) {
    this.filtersForBlock.push(
      filterInstance(
        FILTERS.PROJECT_TECHNOLOGY,
        projects.map((p) => {
          return { label: p, id: p } as MenuItem;
        }),
        {
          switchMode: true,
          maxLabels: this.maxLabelsInFilters,
          isMulti: true,
          searchable: true
        }
      )
    );
    this.registerDateFilter(FILTERS.PROJECT_DATE_FROM);
    this.registerDateFilter(FILTERS.PROJECT_DATE_TO);
    this.registerNumberFilter(FILTERS.PROJECT_HOURS_MIN);
    this.registerNumberFilter(FILTERS.PROJECT_HOURS_MAX);
  }

  private registerNumberFilter(filterKey: FILTERS): void {
    const numberFilterParams: NumberFilterParameters = {
      defaultOperation: { ...EXACTLY_OPERATION },
      operations: [{ ...EXACTLY_OPERATION }],
      inputFieldStep: 1,
      inputFieldsWithArrows: true
    };

    this.filtersForBlock.push(
      filterInstance(filterKey, [{ label: 'test', id: 'test' } as MenuItem], {
        switchMode: true,
        adminOnly: true,
        isMulti: true,
        maxLabels: this.maxLabelsInFilters,
        filterType: 'number',
        numberFilterParams: numberFilterParams,
        filters: [filterKey],
        methodNeeded: false,
        operationNeeded: true
      })
    );
  }

  private registerDateFilter(filterKey: FILTERS): void {
    const dateFilterParams: DateFilterParameters = {
      defaultOperation: { ...EXACTLY_OPERATION },
      operations: [{ ...EXACTLY_OPERATION }],
      minYear: new Date().getFullYear() - 10,
      maxYear: new Date().getFullYear() + 10,
      iconAt: 'right'
    };

    this.filtersForBlock.push(
      filterInstance(filterKey, [], {
        switchMode: true,
        adminOnly: true,
        isMulti: true,
        maxLabels: this.maxLabelsInFilters,
        filterType: 'date',
        dateFilterParams: dateFilterParams,
        filters: [filterKey],
        methodNeeded: false,
        operationNeeded: true
      })
    );
  }

  private applyFilter() {
    ignorePromise(this.router.navigate(['projects'], { queryParams: this.filter }));
    this.reload();
    this.cd.detectChanges();
  }

  /**
   * Searches for a name to serve the autocomplete dropdown.
   * @param text
   */
  protected searchForName = (text: string): Observable<MenuItem[]> => {
    return this.service.autocomplete(text).pipe(
      map((suggest: string[]) => {
        return suggest.map((item) => {
          return textAsMenuItem(item);
        });
      })
    );
  };

  filterBlockChanged(value: Record<string, string>) {
    if (!this.initialized) {
      return;
    }
    const oldFilter = this.filterToString(); // the current filter
    this.filter = updateProjectFilter(this.filter, value);
    const newFilter = this.filterToString(); // the new filter
    // the update is also triggered after a reload, and when there is no real change, then we should not
    // apply the filter, which would cause a reload of the same data and inducing an infinite loop
    if (oldFilter !== newFilter && this.initialized) {
      this.filter.pageNumber = 0;
      this.applyFilter();
    }
  }

  private filterToString() {
    return JSON.stringify({ ...this.filter, tab: undefined, projectCode: undefined });
  }

  public downloadTheList() {
    ignorePromise(
      this.backendService.downloadList(
        {
          ...this.filter,
          forDownload: true
        },
        projectDownloadMapper,
        'projects',
        this.pagination.totalItems
      )
    );
  }

  clickedOnProject(item: Project) {
    ignorePromise(
      addParamForRoute(this.router, this.route, { projectCode: item.id }).then(() => {
        this.selectedProject = item;
      })
    );
    this.cd.detectChanges();
  }

  closeDrawer() {
    ignorePromise(addParamForRoute(this.router, this.route, { tab: null, projectCode: null }));
    this.selectedProject = undefined;
  }

  public override onReloaded() {
    super.onReloaded();
    const allProjects = Array.from(this.items.values()).flat();
    const found = allProjects.find((customer) => {
      return customer.id === this.projectCode;
    });
    if (found) {
      this.clickedOnProject(found);
    }
  }

  public getProjectListItem(projectItem: Project) {
    let data: ListItem = {
      inactive: projectItem.isInactive,
      isAdmin: this.isAdmin,
      columns: [
        {
          lines: [
            {
              content: projectItem.name,
              title: this.translate.instant('viewCustomer'),
              strong: true
            },
            {
              content: projectItem.id || 'n/a'
            }
          ]
        },
        {
          alignSm: ListItemColumnAlignment.right,
          lines: [
            {
              content: projectItem.customerName,
              strong: true
            },
            {
              content:
                '<strong>' +
                projectItem.projectParticipants.length +
                '</strong> ' +
                this.translate.instant('employee.plural')
            }
          ]
        },
        {
          hideIfNotAdmin: true,
          alignSm: ListItemColumnAlignment.right,
          lines: [
            {
              content:
                formatNumber(Number(projectItem.sumHours), 'de', '1.0-2') + ' ' + this.translate.instant('hours'),
              strong: true
            }
          ]
        }
      ]
    };
    return data;
  }
}
